import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowRightIcon,
  FileSearchIcon,
  FileTextIcon,
  FolderOpenIcon,
  LayoutDashboardIcon,
  MessageCircleIcon,
  ScaleIcon,
  ScanSearchIcon,
  UploadIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

// ---------------------------------------------------------------------------
// Capability definitions
// ---------------------------------------------------------------------------

interface Capability {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  summary: string;
  details: string[];
  linkTo: string;
  linkLabel: string;
}

const CAPABILITIES: Capability[] = [
  {
    id: "projects",
    title: "Upload & organize documents",
    icon: UploadIcon,
    color: "bg-emerald-100 text-emerald-700",
    summary:
      "Create projects to organize your documents. Upload files and ForgeIQ automatically classifies them into the right folders.",
    details: [
      "Create projects for each deal, asset, or portfolio you're working on",
      "Upload documents in bulk -- PDFs, spreadsheets, Word docs, and more",
      "AI auto-classifies documents into the right folder categories",
      "Track document counts, upload dates, and processing status",
    ],
    linkTo: "/projects/",
    linkLabel: "Go to Projects",
  },
  {
    id: "questions",
    title: "Define your question bank",
    icon: FileSearchIcon,
    color: "bg-blue-100 text-blue-700",
    summary:
      "Build a reusable library of due diligence questions. Each question defines what to look for, where to look, and how to score results.",
    details: [
      "Create questions organized by category (e.g. PPA, ITC, Interconnection)",
      "Each question has AI instructions for extracting answers from documents",
      "Reuse questions across multiple analyses and projects",
      "Export question banks to share across your organization",
    ],
    linkTo: "/question-bank/",
    linkLabel: "Go to Question Bank",
  },
  {
    id: "analysis",
    title: "Run AI-powered analysis",
    icon: ScanSearchIcon,
    color: "bg-violet-100 text-violet-700",
    summary:
      "The Analysis Builder runs your question bank against project documents. The AI reads every document and extracts structured answers with source citations.",
    details: [
      "Select questions and target projects, then run the analysis",
      "AI reads all relevant documents and extracts answers",
      "Every answer includes source citations you can verify",
      "Generate reports comparing results across projects",
    ],
    linkTo: "/builder/",
    linkLabel: "Go to Analysis Builder",
  },
  {
    id: "dashboards",
    title: "Build dashboards",
    icon: LayoutDashboardIcon,
    color: "bg-amber-100 text-amber-700",
    summary:
      "Create visual dashboards with charts, tables, and KPI widgets to track key metrics across your projects.",
    details: [
      "Widgets: bar charts, pie charts, line charts, tables, KPI cards, and text boxes",
      "Create custom dashboards to visualize your analysis data",
      "Share dashboards with your team",
    ],
    linkTo: "/dashboards/",
    linkLabel: "Go to Dashboards",
  },
  {
    id: "chat",
    title: "Chat with your documents",
    icon: MessageCircleIcon,
    color: "bg-teal-100 text-teal-700",
    summary:
      "Ask free-form questions about any of your uploaded documents. The AI searches across your project files and answers with citations.",
    details: [
      "Ask questions in natural language about any uploaded document",
      "AI searches across your project files using vector search",
      "Every answer includes references to the source document and page",
      "Save conversations to come back to later",
    ],
    linkTo: "/chats",
    linkLabel: "Go to Chat",
  },
  {
    id: "rulesets",
    title: "Define rule sets",
    icon: ScaleIcon,
    color: "bg-rose-100 text-rose-700",
    summary:
      "Create rule sets that define compliance criteria or scoring logic. Apply them to analysis reports to flag issues automatically.",
    details: [
      "Define rules with conditions and thresholds",
      "Apply rules to analysis reports for automated compliance checking",
      "Track pass/fail status across projects",
    ],
    linkTo: "/rule-sets/",
    linkLabel: "Go to Rule Sets",
  },
  {
    id: "org-context",
    title: "Add organization context",
    icon: FolderOpenIcon,
    color: "bg-indigo-100 text-indigo-700",
    summary:
      "Upload reference documents that give the AI background context about your organization's standards, policies, or preferences. This context feeds into analysis to improve answer quality.",
    details: [
      "Upload internal guidelines, checklists, or reference materials",
      "Context documents are used by the AI during analysis",
      "Improves AI answer quality and alignment with your standards",
      "Organize context into collections by topic",
    ],
    linkTo: "/org-context/",
    linkLabel: "Go to Org Context",
  },
  {
    id: "reports",
    title: "Generate reports",
    icon: FileTextIcon,
    color: "bg-pink-100 text-pink-700",
    summary:
      "Create structured reports from the Analysis section. Reports summarize findings across all questions and projects in a shareable format.",
    details: [
      "Generate reports from completed analyses in Analysis Builder",
      "Reports include all extracted answers with source citations",
      "Build dashboards directly from report data",
      "Export or share reports with your team",
    ],
    linkTo: "/builder/",
    linkLabel: "Go to Analysis Builder",
  },
];

// ---------------------------------------------------------------------------
// Typical workflow steps
// ---------------------------------------------------------------------------

const WORKFLOW_STEPS = [
  {
    step: 1,
    label: "Create a project and upload documents",
    description: "Start by creating a project and uploading the relevant documents -- PPAs, interconnection agreements, environmental reports, etc.",
    linkTo: "/projects/",
  },
  {
    step: 2,
    label: "Initiate a context aware analysis",
    description: "Upload a document or select a document you've uploaded to create the framework for an analysis report.",
    linkTo: "/org-context/",
  },
  {
    step: 3,
    label: "Run an analysis",
    description: "Select your questions and target projects, then let the AI extract structured answers from every document.",
    linkTo: "/builder/",
  },
  {
    step: 4,
    label: "Review results and build dashboards",
    description: "Review the AI's answers (each with source citations), generate reports, and build visual dashboards to track KPIs.",
    linkTo: "/dashboards/",
  },
  {
    step: 5,
    label: "Iterate and compare",
    description: "Add more documents, re-run analyses, compare dashboards across projects, and refine your question bank over time.",
    linkTo: "/builder/",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface GettingStartedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GettingStartedDialog({
  open,
  onOpenChange,
}: GettingStartedDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            How ForgeIQ works
          </DialogTitle>
          <DialogDescription>
            ForgeIQ is an AI-powered platform for renewable energy due diligence.
            Upload documents, run analyses, and build dashboards -- all in one place.
          </DialogDescription>
        </DialogHeader>

        {/* Typical workflow */}
        <div className="mt-4">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Typical workflow
          </h3>
          <ol className="space-y-3">
            {WORKFLOW_STEPS.map((ws) => (
              <li key={ws.step}>
                <Link
                  to={ws.linkTo}
                  className="flex gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50"
                  onClick={() => onOpenChange(false)}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cf-foreground text-xs font-semibold text-white">
                    {ws.step}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {ws.label}
                    </p>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {ws.description}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </div>

        {/* Capabilities accordion */}
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            What you can do
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {CAPABILITIES.map((cap) => (
              <AccordionItem key={cap.id} value={cap.id}>
                <AccordionTrigger className="text-sm hover:no-underline">
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-lg ${cap.color}`}
                    >
                      <cap.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{cap.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pl-11">
                  <p className="mb-2 text-sm text-muted-foreground">
                    {cap.summary}
                  </p>
                  <ul className="mb-3 space-y-1">
                    {cap.details.map((detail, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-xs text-muted-foreground"
                      >
                        <span className="mt-1 block h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={cap.linkTo}
                    className="inline-flex items-center gap-1 text-xs font-medium text-cf-foreground hover:underline"
                    onClick={() => onOpenChange(false)}
                  >
                    {cap.linkLabel}
                    <ArrowRightIcon className="h-3 w-3" />
                  </Link>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  );
}
